import axios, { AxiosRequestConfig } from "axios";
import { toast } from "sonner";
import { API_URL } from "@client/utils/apiUrl";

axios.defaults.withCredentials = true;

// === CSRF interceptor ===
// Server vyžaduje X-CSRF-Token header pro state-changing metody.
// Token získáme z /api/csrf-token endpointu, server zároveň nastaví doprovodný cookie.
// Po 403 EBADCSRFTOKEN tokeny refresh (např. session vypršel).

let csrfToken: string | null = null;
let csrfFetchPromise: Promise<string | null> | null = null;

const fetchCsrfToken = async (): Promise<string | null> => {
  // Deduplikace concurrent requests
  if (csrfFetchPromise) return csrfFetchPromise;
  csrfFetchPromise = (async () => {
    try {
      const res = await axios.get<{ token: string }>(`${API_URL}/api/csrf-token`, { withCredentials: true });
      return res.data.token;
    } catch {
      return null;
    } finally {
      csrfFetchPromise = null;
    }
  })();
  return csrfFetchPromise;
};

axios.interceptors.request.use(async (config) => {
  const method = (config.method ?? "get").toLowerCase();
  if (["post", "put", "delete", "patch"].includes(method)) {
    if (!csrfToken) {
      csrfToken = await fetchCsrfToken();
    }
    if (csrfToken) {
      config.headers.set("X-CSRF-Token", csrfToken);
    }
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    // Aborted requests (AbortController.abort) — legitimate, callers handle via axios.isCancel.
    // Bez tohoto guardu by každý unmount/pan/navigace tetoval toast "Server není dostupný",
    // protože CanceledError nemá .response a spadl by do network-down větve dole.
    if (axios.isCancel(err)) throw err;
    const status = err.response?.status as number | undefined;
    const code = err.response?.data?.responseObject?.code ?? err.response?.data?.code;
    if (status === 403 && code === "EBADCSRFTOKEN") {
      // Token vypršel; obnovit a říct uživateli
      csrfToken = null;
      toast.error("Bezpečnostní token vypršel. Obnov stránku.");
    }
    if (status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }
    if (!err.response) {
      // Network/server down
      toast.error("Server není dostupný.");
    }
    throw err;
  }
);

/**
 * Fire-and-forget POST, který přežije zavření stránky / přechod na pozadí.
 *
 * Axios (XHR) tohle neumí — request se při `pagehide` zruší. Používá se pro
 * kritický `ptzStopRun`: kdyby se zahodil, kamera by se točila dál a nikdo by o
 * tom nevěděl. `keepalive: true` prohlížeči řekne, že request smí doběhnout i po
 * zániku dokumentu.
 *
 * Bez retry, bez toastů, bez čtení odpovědi — v tom okamžiku už není komu je ukázat.
 * CSRF token bere z téže cache jako axios interceptor; když ještě není načtený,
 * request se pošle bez něj (server ho odmítne, ale to je lepší než nic neposlat).
 */
export const postKeepalive = (url: string, data?: unknown): void => {
  try {
    void fetch(url, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
      body: JSON.stringify(data ?? {}),
    }).catch(() => undefined);
  } catch {
    /* fetch není k dispozici — nic lepšího už udělat nelze */
  }
};

export const getBase = async <T>(
  url: string,
  config?: AxiosRequestConfig<unknown>,
  params?: Record<string, unknown>
): Promise<T> => await (await axios.get(url, { ...config, withCredentials: true, params: params })).data;
export const postBase = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig<unknown>): Promise<T> =>
  await (
    await axios.post(url, data, { ...config, withCredentials: true })
  ).data;

export const get =
  <T>(path: string, params?: Record<string, unknown>, url?: string, config?: AxiosRequestConfig<unknown>) =>
  (dispatch: (data: T) => void) => {
    getBase<T>(`${url ?? API_URL}${path}`, config, params).then(
      (data) => {
        dispatch(data);
      },
      (err) => {
        console.error(err);
      }
    );
  };

export const post =
  <T>(path: string, data?: unknown, url?: string, config?: AxiosRequestConfig<unknown>) =>
  (dispatch: (data: T, err?: unknown) => void) => {
    postBase<T>(`${url ?? API_URL}${path}`, data, config).then(
      (data) => {
        dispatch(data as T);
      },
      (err) => {
        console.error(err);
        dispatch(undefined as T, err);
      }
    );
  };

export const postAsync = <T>(path: string, data?: unknown, url?: string) =>
  postBase<T>(`${url ?? API_URL}${path}`, data);
export const getAsync = <T>(path: string, url?: string) => getBase<T>(`${url ?? API_URL}${path}`);

/**
 * `postAsync` varianta s `AbortSignal` support — viz docs/plans/CHART-DATA-REFACTOR.md.
 * Použito hlavně v `useChartData` pro abort stale fetchů při změně bounds / unmount.
 * Volající musí ošetřit `axios.isCancel(err)` (`AbortController.abort()` shodí `CanceledError`).
 */
export const postAsyncSignal = <T>(path: string, data: unknown, signal: AbortSignal, url?: string): Promise<T> =>
  postBase<T>(`${url ?? API_URL}${path}`, data, { signal });

// Symetrické base helpery k get/postBase. CSRF token + .data extrakce dědí z default
// axios instance (interceptory registrované na ní) — žádná duplikace CSRF logiky.
export const putBase = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig<unknown>): Promise<T> =>
  (await axios.put(url, data, { ...config, withCredentials: true })).data;
export const patchBase = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig<unknown>): Promise<T> =>
  (await axios.patch(url, data, { ...config, withCredentials: true })).data;
// DELETE s tělem (axios bere body přes config.data) — kvůli např. computed delete { keepData }.
export const deleteBase = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig<unknown>): Promise<T> =>
  (await axios.delete(url, { ...config, withCredentials: true, data })).data;

export const putAsync = <T>(path: string, data?: unknown, url?: string) =>
  putBase<T>(`${url ?? API_URL}${path}`, data);
export const patchAsync = <T>(path: string, data?: unknown, url?: string) =>
  patchBase<T>(`${url ?? API_URL}${path}`, data);
/** `data` = volitelné tělo DELETE (např. deleteAsync(path, { keepData })). */
export const deleteAsync = <T>(path: string, data?: unknown, url?: string) =>
  deleteBase<T>(`${url ?? API_URL}${path}`, data);
